import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {  useLocation, useNavigate } from 'react-router-dom';
import { useCategories } from '../../../hooks/useCategories';
import { useFilters } from '../../../hooks/useFilters';
import { useTranslatedContent } from '../../../hooks/useTranslatedContent';
import {
  CategoriesData,
  Category,
  Subcategory,
} from '../../../types/categories';
import { useThemeStore } from '../../../stores/themeStore';
import styles from './Categories.module.less';

import PathInfo from '../PathInfo/PathInfo';

const Categories: React.FC = () => {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { t } = useTranslation();
  const { data: categoriesData, isError, error } = useCategories();
  const {
    selectedCategory,
    selectedSubcategories,
    setSelectedCategory,
    setSelectedSubcategories,
  } = useFilters();
  const { getTranslation } = useTranslatedContent();
  const location = useLocation();
  const { theme } = useThemeStore();


  const navigate = useNavigate();

  const pathSegments = location.pathname.split('/').filter(Boolean);
  const categorySlug = pathSegments[0];
  const category = categoriesData?.categories.find(
    (cat) => cat.slug === categorySlug
  );

  const showBottom = location.pathname === '/';
  const pageSlug =
    pathSegments[0] === category?.slug ? pathSegments[1] : pathSegments[0];

  const isMainPage = pathSegments.length === 0;
  const isCategoryPage = categoriesData?.categories.some(
    (category) => `/${category.slug}` === location.pathname
  );
  const shouldShow = !isMainPage && !isCategoryPage;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setIsAccordionOpen(!!selectedCategory);
  }, [selectedCategory]);

  const typedCategoriesData = categoriesData as CategoriesData | undefined;

  const handleCategoryClick = (categoryId: string, categorySlug: string) => {
    if (isMobile) {
      if (selectedCategory === categoryId) {
        setIsAccordionOpen(!isAccordionOpen);
        if (!isAccordionOpen) {
          setSelectedCategory(categoryId);
          navigate(`/${categorySlug}`);
        } else {
          setSelectedCategory(null);
          navigate('/');
        }
      } else {
        setIsAccordionOpen(true);
        setSelectedCategory(categoryId);
        navigate(`/${categorySlug}`);
        setSelectedSubcategories([]);
      }
    } else {
      setSelectedCategory(categoryId);
      navigate(`/${categorySlug}`);

      setSelectedSubcategories([]);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const squareBlockClick = () => {
    setSelectedCategory(null);
    setSelectedSubcategories([]);
    navigate('/');
  };

  const handleSubcategoryClick = (subcategoryId: string) => {
    setSelectedSubcategories((prev) =>
      prev.includes(subcategoryId)
        ? prev.filter((id) => id !== subcategoryId)
        : [...prev, subcategoryId]
    );
  };

  const selectedCategoryData = useMemo(() => {
    if (!selectedCategory || !typedCategoriesData) return null;
    return typedCategoriesData.categories.find(
      (category) => category.id === selectedCategory
    );
  }, [selectedCategory, typedCategoriesData]);

  const filteredSubcategories = useMemo(() => {
    if (!selectedCategory || !typedCategoriesData) return [];
    return typedCategoriesData.subcategories.filter((subcategory) =>
      subcategory.categories.some(
        (cat) => cat.categories_id === selectedCategory
      )
    );
  }, [selectedCategory, typedCategoriesData]);

  if (isError) {
    return <div>Error: {(error as Error).message}</div>;
  }

  if (!typedCategoriesData) {
    return null;
  }

  return (
    <div
      className={`${styles.categoryComponent}
      ${showBottom && styles.categoriesContainerOtherPage}
       `}
    >
      <div
        className={`${styles.categoriesContainer} ${shouldShow && styles.categoriesContainerPath} `}
      >
        <div className={styles.logoContainer}>
          <h2 className={styles.logo} onClick={squareBlockClick}>
            {t('Fund')}
          </h2>
          <span className={styles.logoDecor}>/</span>
        </div>

        {!shouldShow ? (
          <>
            <div
              className={`${styles.categories} ${theme === 'dark' && styles.categoriesDark}`}
            >
              {!isMobile && selectedCategoryData ? (
                <>
                  <div className={styles.categoryButtonContainer}>
                    <button
                      className={`${styles.categoryButton} ${styles.categoryButtonActive}`}
                      onClick={() =>
                        handleCategoryClick(
                          selectedCategoryData.id,
                          selectedCategoryData.slug
                        )
                      }
                    >
                      {getTranslation(selectedCategoryData, 'category_name')}
                    </button>
                    <span className={styles.categoriesDecor}>/</span>
                  </div>

                  <div
                    className={`${styles.subcategoryList} ${styles.subcategoryListOpen}`}
                  >
                    <ul className={styles.subcategoryList}>
                      {filteredSubcategories.map((subcategory: Subcategory) => (
                        <React.Fragment key={subcategory.id}>
                          <li className={styles.categoryButtonContainer}>
                            <button
                              className={`${styles.categoryButton} ${styles.subcategoryButton} ${
                                selectedSubcategories.includes(subcategory.id)
                                  ? styles.subcategoryButtonActive
                                  : ''
                              }`}
                              onClick={() =>
                                handleSubcategoryClick(subcategory.id)
                              }
                            >
                              {getTranslation(subcategory, 'subcategory_name')}
                            </button>
                          </li>
                          <span className={styles.subcategoryDecor}>/</span>
                        </React.Fragment>
                      ))}
                    </ul>
                  </div>
                </>
              ) : (
                <ul className={styles.mainCategories}>
                  {(!selectedCategory
                    ? typedCategoriesData.categories
                    : typedCategoriesData.categories.filter(
                        (cat) => cat.id === selectedCategory
                      )
                  ).map((category: Category) => (
                    <React.Fragment key={category.id}>
                      <li className={styles.categoryButtonContainer}>
                        <button
                          className={`${styles.categoryButton} ${
                            selectedCategory === category.id
                              ? styles.categoryButtonActive
                              : ''
                          } ${
                            selectedCategory === category.id && isAccordionOpen
                              ? styles.open
                              : ''
                          }`}
                          onClick={() =>
                            handleCategoryClick(category.id, category.slug)
                          }
                        >
                          {getTranslation(category, 'category_name')}
                          {selectedCategory === category.id &&
                            isAccordionOpen && (
                              <span className={styles.categoriesDecorMobile}>
                                /
                              </span>
                            )}
                        </button>
                        {selectedCategory === category.id && (
                          <div
                            className={`${styles.subcategoryList} ${
                              isAccordionOpen ? styles.subcategoryListOpen : ''
                            }`}
                          >
                            <ul>
                              {filteredSubcategories.map(
                                (subcategory: Subcategory) => (
                                  <li
                                    key={subcategory.id}
                                    className={styles.categoryButtonContainer}
                                  >
                                    <button
                                      className={`${styles.categoryButton} ${
                                        styles.subcategoryButton
                                      } ${
                                        selectedSubcategories.includes(
                                          subcategory.id
                                        )
                                          ? styles.subcategoryButtonActive
                                          : ''
                                      }`}
                                      onClick={() =>
                                        handleSubcategoryClick(subcategory.id)
                                      }
                                    >
                                      {getTranslation(
                                        subcategory,
                                        'subcategory_name'
                                      )}
                                    </button>
                                  </li>
                                )
                              )}
                            </ul>
                          </div>
                        )}
                      </li>
                      {!isMobile && (
                        <span className={styles.subcategoryDecor}>/</span>
                      )}
                    </React.Fragment>
                  ))}
                </ul>
              )}
              {categorySlug === 'legal-strand-ua' && (
                <button
                  onClick={() => navigate('/legal/zapovit')}
                  style={{
                    marginLeft: '12px',
                    padding: '4px 12px',
                    border: '1px solid #000',
                    background: 'transparent',
                    color: '#000',
                    cursor: 'pointer',
                    font: 'inherit',
                    borderRadius: '2px',
                    whiteSpace: 'nowrap',
                    alignSelf: 'center',
                  }}
                >
                  Заповіт
                </button>
              )}
            </div>
          </>
        ) : (
          <PathInfo category={category} pageSlug={pageSlug} />
        )}
      </div>
    </div>
  );
};

export default React.memo(Categories);
